<script setup lang="ts">
import { computed } from 'vue'

/**
 * Content card: a bordered, rounded, white surface for grouping content (account sections, offline
 * data cards, invite rows). Single-root <div> so extra classes / listeners fall through; content via
 * the default slot. Consolidates the ad-hoc `rounded-* border bg-white p-* shadow-sm` boxes onto one
 * surface and the single neutral (gray) per the palette plan.
 *
 * Tone/alert boxes (yellow/red/gray callouts) and the floating bottom sheets are deliberately NOT
 * folded in here — they're separate concerns (a callout and BaseBottomSheet respectively).
 */
interface Props {
  radius?: 'lg' | 'xl' | '2xl'
  /** none (table/custom padding) · sm (p-4) · md (p-6, page sections) · lg (p-8). */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  shadow?: boolean
}
const props = withDefaults(defineProps<Props>(), { radius: '2xl', padding: 'md', shadow: true })

const radiusClass = computed(
  () => ({ lg: 'rounded-lg', xl: 'rounded-xl', '2xl': 'rounded-2xl' })[props.radius],
)
const paddingClass = computed(() => ({ none: '', sm: 'p-4', md: 'p-6', lg: 'p-8' })[props.padding])
</script>

<template>
  <div
    class="border border-gray-200 bg-white"
    :class="[radiusClass, paddingClass, props.shadow ? 'shadow-sm' : '']"
  >
    <slot />
  </div>
</template>
